import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, pluginsApi, type ValidationErrorEntry } from "../api/client";
import HereNowConnectCard from "../components/HereNowConnectCard";
import { authApi } from "../api/auth";
import type { Plugin } from "../types";
import "./PluginsView.css";
import { useToast } from "../lib/toast";

interface EditState {
  id: string;
  name: string;
  description: string;
  manifest: string;
}

interface ManifestTool {
  name: string;
  description?: string;
  requires_confirmation?: boolean;
}

const EMPTY_MANIFEST = "{\n  \n}";

/** Task 9.2 — render a pydantic 422's `detail` array structurally instead
 * of flattening it into one alert/toast string. */
function ValidationErrors({ errors }: { errors: ValidationErrorEntry[] }) {
  return (
    <ul className="plugin-validation-errors">
      {errors.map((e, i) => (
        <li key={i}>
          <code>{(e.loc || []).join(".")}</code>: {e.msg}
        </li>
      ))}
    </ul>
  );
}

export default function PluginsView() {
  const toast = useToast();
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  // Task P2 — GET /api/plugins now returns a slim shape (no manifest
  // internals) to non-admins; mirror that here by hiding every
  // manifest-editing / install / enable-disable control unless the
  // logged-in user is actually an admin. Defaults to false (the safer,
  // more restrictive rendering) until /api/auth/me resolves.
  const [isAdmin, setIsAdmin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [manifest, setManifest] = useState(EMPTY_MANIFEST);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [repoUrl, setRepoUrl] = useState("");
  const [installing, setInstalling] = useState(false);
  const [allowlist, setAllowlist] = useState<string[] | null>(null);
  const [createErrors, setCreateErrors] = useState<ValidationErrorEntry[] | null>(null);
  const [editErrors, setEditErrors] = useState<ValidationErrorEntry[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setPlugins(await pluginsApi.list());
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load();
    authApi
      .me()
      .then((u) => setIsAdmin(u.role === "admin"))
      .catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    pluginsApi.allowlist().then(setAllowlist).catch(() => setAllowlist([]));
  }, [isAdmin]);

  // Task 9.2 — the "allowlisted-host hint" for the repo-URL install field.
  const repoHost = useMemo(() => {
    try {
      return new URL(repoUrl.trim()).hostname.toLowerCase();
    } catch {
      return null;
    }
  }, [repoUrl]);
  const isAllowlistedHost =
    !repoUrl.trim() || allowlist === null || allowlist.length === 0 || (repoHost !== null && allowlist.includes(repoHost));

  /** Extracts a pydantic 422 detail array from a caught error, if that's
   * what it is; otherwise falls back to the flattened toast the way every
   * other view here handles errors. */
  function detailOrToast(e: unknown): ValidationErrorEntry[] | null {
    if (e instanceof ApiError && e.status === 422 && Array.isArray(e.detail)) {
      return e.detail as ValidationErrorEntry[];
    }
    toast.error((e as Error).message);
    return null;
  }

  const create = async () => {
    setCreateErrors(null);
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(manifest);
    } catch {
      toast.error("Manifest is not valid JSON");
      return;
    }
    try {
      await pluginsApi.create({ name, description, manifest: parsed });
      setName("");
      setDescription("");
      setManifest(EMPTY_MANIFEST);
      setShowForm(false);
      load();
    } catch (e) {
      setCreateErrors(detailOrToast(e));
    }
  };

  const saveEdit = async () => {
    if (!editState) return;
    setEditErrors(null);
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(editState.manifest);
    } catch {
      toast.error("Manifest is not valid JSON");
      return;
    }
    try {
      await pluginsApi.update(editState.id, {
        name: editState.name,
        description: editState.description,
        manifest: parsed,
      });
      setEditState(null);
      load();
    } catch (e) {
      setEditErrors(detailOrToast(e));
    }
  };

  const toggleEnabled = async (p: Plugin) => {
    try {
      const updated = p.enabled ? await pluginsApi.disable(p.id) : await pluginsApi.enable(p.id);
      setPlugins((list) => list.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const sync = async () => {
    try {
      await pluginsApi.sync();
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this plugin?")) return;
    await pluginsApi.remove(id);
    load();
  };

  const startEdit = (p: Plugin) => {
    setEditState({
      id: p.id,
      name: p.name,
      description: p.description,
      manifest: p.manifest ? JSON.stringify(p.manifest, null, 2) : EMPTY_MANIFEST,
    });
    setShowForm(false);
  };

  const installRepo = async () => {
    if (!repoUrl.trim()) return;
    setInstalling(true);
    try {
      await pluginsApi.installRepo(repoUrl.trim());
      setRepoUrl("");
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setInstalling(false);
    }
  };

  const installUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setInstalling(true);
    try {
      await pluginsApi.installUpload(files[0]);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">🧩 Plugins</h2>
          <p className="view-subtitle">
            {isAdmin ? "Anthropic-style plugin manifests" : "Plugins available in this workspace"}
          </p>
        </div>
        {isAdmin && (
          <div className="row">
            <button className="btn btn-sm" onClick={sync}>↻ Sync Directory</button>
            <button className="btn btn-primary" onClick={() => { setShowForm((s) => !s); setEditState(null); }}>
              {showForm ? "Cancel" : "+ New Plugin"}
            </button>
          </div>
        )}
      </div>

      <HereNowConnectCard />

      {isAdmin && (
        <div className="card plugin-install-card" style={{ marginBottom: "var(--gap-lg)" }}>
          <h3 className="settings-section-title">Install</h3>
          <div className="col">
            <div>
              <label className="form-label">Install from repo URL</label>
              <div className="row">
                <input
                  className="input"
                  placeholder="https://github.com/owner/repo"
                  value={repoUrl}
                  onChange={(e) => setRepoUrl(e.target.value)}
                />
                <button
                  className="btn btn-primary"
                  onClick={installRepo}
                  disabled={installing || !repoUrl.trim() || !isAllowlistedHost}
                  title={!isAllowlistedHost ? `Host not in the allowlist (${(allowlist || []).join(", ")})` : undefined}
                >
                  {installing ? <span className="spinner" /> : "Install"}
                </button>
              </div>
              {allowlist && allowlist.length > 0 && (
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  Allowlisted hosts: {allowlist.join(", ")}
                  {!isAllowlistedHost && (
                    <span className="plugin-host-warning"> — "{repoHost}" is not allowed.</span>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="form-label">Upload archive (.tar.gz / .zip)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".tar.gz,.tgz,.zip,application/gzip,application/zip"
                style={{ display: "none" }}
                onChange={(e) => installUpload(e.target.files)}
              />
              <button
                className="btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={installing}
              >
                Choose File…
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && showForm && (
        <div className="card fade-in" style={{ marginBottom: "var(--gap-lg)" }}>
          <div className="col">
            <div>
              <label className="form-label">Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Description</label>
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Manifest (JSON)</label>
              <textarea
                className="textarea"
                style={{ minHeight: 180, fontFamily: "var(--mono)" }}
                value={manifest}
                onChange={(e) => setManifest(e.target.value)}
              />
            </div>
            {createErrors && <ValidationErrors errors={createErrors} />}
            <button className="btn btn-primary" onClick={create}>Create Plugin</button>
          </div>
        </div>
      )}

      {isAdmin && editState && (
        <div className="card fade-in" style={{ marginBottom: "var(--gap-lg)" }}>
          <h3 className="settings-section-title">Edit Plugin</h3>
          <div className="col">
            <div>
              <label className="form-label">Name</label>
              <input className="input" value={editState.name} onChange={(e) => setEditState({ ...editState, name: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Description</label>
              <input className="input" value={editState.description} onChange={(e) => setEditState({ ...editState, description: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Manifest (JSON)</label>
              <textarea
                className="textarea"
                style={{ minHeight: 180, fontFamily: "var(--mono)" }}
                value={editState.manifest}
                onChange={(e) => setEditState({ ...editState, manifest: e.target.value })}
              />
            </div>
            {editErrors && <ValidationErrors errors={editErrors} />}
            <div className="row">
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
              <button className="btn" onClick={() => setEditState(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {plugins.length === 0 && (
        <div className="empty-state">
          <h3>No plugins</h3>
          <p>
            {isAdmin
              ? "Sync from the directory, install from a repo, or create one manually."
              : "No plugins are enabled in this workspace yet."}
          </p>
        </div>
      )}

      <div className="col">
        {plugins.map((p) => (
          <div key={p.id} className="card plugin-row">
            <div style={{ flex: 1 }}>
              <div className="row" style={{ gap: 10, marginBottom: 4 }}>
                <strong>{p.name}</strong>
                {p.version && <span className="tag tag-muted">v{p.version}</span>}
                <span className={`tag ${p.enabled ? "tag-success" : "tag-muted"}`}>
                  {p.enabled ? "enabled" : "disabled"}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>{p.description}</div>
              {isAdmin && Array.isArray((p.manifest as { tools?: ManifestTool[] } | null)?.tools) && (
                <div className="plugin-tools">
                  {((p.manifest as { tools: ManifestTool[] }).tools).map((t) => (
                    <span key={t.name} className="tag tag-muted plugin-tool-tag" title={t.description}>
                      {t.name}
                      {t.requires_confirmation !== false && (
                        <span className="plugin-tool-confirm" title="Requires confirmation before running">
                          {" "}⚠️
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {/* Task P2 — non-admin slim shape has no manifest at all, just
                  the flat tool_names/command_names lists. */}
              {!isAdmin && Array.isArray(p.tool_names) && p.tool_names.length > 0 && (
                <div className="plugin-tools">
                  {p.tool_names.map((name) => (
                    <span key={name} className="tag tag-muted plugin-tool-tag">{name}</span>
                  ))}
                </div>
              )}
              {isAdmin && p.manifest && (
                <details className="plugin-manifest">
                  <summary>manifest</summary>
                  <pre>{JSON.stringify(p.manifest, null, 2)}</pre>
                </details>
              )}
            </div>
            {isAdmin && (
              <div className="col" style={{ gap: 6 }}>
                <button className="btn btn-sm" onClick={() => toggleEnabled(p)}>
                  {p.enabled ? "Disable" : "Enable"}
                </button>
                <button className="btn btn-sm" onClick={() => startEdit(p)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => remove(p.id)}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}